/**
 * 阿里云 ARMS RUM 初始化模块：负责按环境变量配置启动前端监控。
 */

import type { IBrowserConfig } from '@arms/rum-browser'
import type { UserInfo } from '@/types/auth'

type RumEnvironment = 'prod' | 'gray' | 'pre' | 'daily' | 'local'

interface RumUserConfig {
  id?: string
  name?: string
}

interface RumSdk {
  init(initConfig: IBrowserConfig): Promise<unknown>
  setConfig(config: Partial<IBrowserConfig>): void
}

interface RumSdkModule {
  default: RumSdk
}

const DEFAULT_SPA_MODE = 'history' as const

const RUM_ENVIRONMENTS = ['prod', 'gray', 'pre', 'daily', 'local'] as const

let hasInitializedRum = false
let rumModulePromise: Promise<RumSdkModule> | null = null
let rumInitPromise: Promise<RumSdk> | null = null
let currentRumUser: RumUserConfig | null = null

/**
 * 判断是否为合法的 RUM 环境值。
 * @param value 待判断的环境值
 * @returns 是否为合法环境值
 */
function isRumEnvironment(value: string): value is RumEnvironment {
  return RUM_ENVIRONMENTS.includes(value as RumEnvironment)
}

/**
 * 获取当前 RUM 环境值，未配置时按构建环境自动推断。
 * @returns RUM 环境值
 */
function getRumEnvironment(): RumEnvironment {
  const rumEnv = import.meta.env.VITE_RUM_ENV

  if (typeof rumEnv === 'string' && isRumEnvironment(rumEnv)) {
    return rumEnv
  }

  return import.meta.env.PROD ? 'prod' : 'local'
}

/**
 * 获取当前 RUM 上报版本号。
 * @returns 版本号或 undefined
 */
function getRumVersion(): string | undefined {
  const rumVersion = import.meta.env.VITE_APP_VERSION?.trim()

  return rumVersion === undefined || rumVersion === '' ? undefined : rumVersion
}

/**
 * 生成 RUM 初始化配置；未配置 endpoint 时返回 null。
 * @returns RUM 配置或 null
 */
function getRumConfig(): IBrowserConfig | null {
  const endpoint = import.meta.env.VITE_RUM_ENDPOINT?.trim()
  const version = getRumVersion()

  if (endpoint === undefined || endpoint === '') {
    return null
  }

  return {
    endpoint,
    env: getRumEnvironment(),
    ...(version === undefined ? {} : { version }),
    spaMode: DEFAULT_SPA_MODE,
    collectors: {
      perf: true,
      webVitals: true,
      api: true,
      staticResource: true,
      jsError: true,
      consoleError: true,
      action: true,
    },
    tracing: false,
  }
}

/**
 * 按需加载阿里云 RUM SDK 模块。
 * @returns RUM SDK 模块
 */
async function loadRumModule(): Promise<RumSdkModule> {
  rumModulePromise ??= import('@arms/rum-browser') as Promise<RumSdkModule>

  return await rumModulePromise
}

/**
 * 执行阿里云 RUM 初始化。
 * @param rumConfig RUM 初始化配置
 * @returns 初始化后的 RUM SDK
 */
async function initRum(rumConfig: IBrowserConfig): Promise<RumSdk> {
  const rumModule = await loadRumModule()

  await rumModule.default.init(rumConfig)

  if (currentRumUser !== null) {
    rumModule.default.setConfig({ user: currentRumUser })
  }

  return rumModule.default
}

/**
 * 将业务用户信息转换为 RUM 用户配置。
 * @param user 当前登录用户
 * @returns RUM 用户配置或 null
 */
function createRumUserConfig(user: UserInfo | null): RumUserConfig | null {
  if (user === null) {
    return null
  }

  return {
    id: user.id,
    name: user.username,
  }
}

/**
 * 同步当前用户信息到阿里云 RUM。
 * @param user 当前登录用户
 * @returns 无返回值
 */
export function syncRumUser(user: UserInfo | null): void {
  currentRumUser = createRumUserConfig(user)

  if (rumInitPromise === null) {
    return
  }

  void rumInitPromise
    .then((rumSdk: RumSdk): void => {
      rumSdk.setConfig({ user: currentRumUser ?? {} })
    })
    .catch((error: unknown): void => {
      console.error('阿里云 RUM 用户信息同步失败', error)
    })
}

/**
 * 初始化阿里云 RUM。
 * @returns 无返回值
 */
export function initializeRum(): void {
  if (hasInitializedRum) {
    return
  }

  const rumConfig = getRumConfig()

  if (rumConfig === null) {
    return
  }

  hasInitializedRum = true

  rumInitPromise ??= initRum(rumConfig)

  void rumInitPromise.catch((error: unknown): void => {
    hasInitializedRum = false
    rumInitPromise = null
    console.error('阿里云 RUM 初始化失败', error)
  })
}
